'use client'

import React, { useLayoutEffect, useRef, useState } from 'react'
import { Plus, Minus } from 'lucide-react'
import { cn } from '@/utilities/ui'
import { outfit } from '@/fonts'

const ACCENT = '#4BA3B0'

type FAQItem = {
  id?: string | null
  question: string
  answer: string
}

type Props = {
  heading: string
  items: FAQItem[]
  className?: string
}

function AccordionItem({
  item,
  index: _index,
  isOpen,
  isLast,
  onToggle,
}: {
  item: FAQItem
  index: number
  isOpen: boolean
  isLast: boolean
  onToggle: () => void
}) {
  const contentRef = useRef<HTMLDivElement>(null)

  // Measure + animate the panel imperatively (DOM = external system) so no
  // ref is read during render.
  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return
    el.style.maxHeight = isOpen ? `${el.scrollHeight}px` : '0px'
    el.style.opacity = isOpen ? '1' : '0'
  }, [isOpen])

  return (
    <div className={cn('border-t border-[#c8c8c8]', isLast && 'border-b')}>
      <dt>
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={onToggle}
          className={cn(
            'flex w-full items-center justify-between gap-6 py-[18px] pl-4 pr-5 text-left transition-colors duration-200',
            isOpen ? 'text-white' : 'text-[#1d1d1b]',
          )}
          style={{ backgroundColor: isOpen ? ACCENT : 'transparent' }}
        >
          <span
            className={cn(outfit.className, 'text-[17px] font-normal leading-snug md:text-[24px]')}
          >
            {item.question}
          </span>
          <span
            className="shrink-0 transition-transform duration-200"
            style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(0deg)' }}
            aria-hidden
          >
            {isOpen ? (
              <Minus className="h-[18px] w-[18px] text-white" strokeWidth={2} />
            ) : (
              <Plus className="h-[18px] w-[18px] text-[#1d1d1b]" strokeWidth={2} />
            )}
          </span>
        </button>
      </dt>
      <dd
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: '0px', opacity: 0 }}
      >
        <div className="pl-4 pr-10 pb-5 pt-2">
          <p className={cn(outfit.className, 'text-[17px] leading-relaxed text-[#555]')}>
            {item.answer}
          </p>
        </div>
      </dd>
    </div>
  )
}

export const FAQClient: React.FC<Props> = ({ heading, items, className }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const headingTrimmed = heading?.trim() ?? ''
  const qaLabel = 'Q.& A.'
  const hasQaPrefix = headingTrimmed.startsWith(qaLabel)
  const headingMain = hasQaPrefix ? headingTrimmed.slice(qaLabel.length).trim() : headingTrimmed

  return (
    <section className={cn('w-full bg-white p-[10px]', className)}>
      <div className="mx-auto max-w-[1140px]">
        {headingTrimmed ? (
          <div className="mb-8">
            {hasQaPrefix && (
              <p
                className={cn(
                  outfit.className,
                  'text-[26px] font-normal text-[#1d1d1b] md:text-[31px]',
                )}
              >
                {qaLabel}
              </p>
            )}
            <h2
              className={cn(
                outfit.className,
                'text-[26px] font-normal text-[#1d1d1b] md:text-[31px]',
              )}
            >
              {headingMain}
            </h2>
            <div
              className="mt-1 h-[10px] w-full md:w-[calc(70vw-42px)] min-[1440px]:w-[calc(40vw-24px)]"
              style={{ backgroundColor: ACCENT }}
              aria-hidden
            />
          </div>
        ) : null}

        <dl className="mt-4">
          {items.map((item, index) => (
            <AccordionItem
              key={item.id ?? index}
              item={item}
              index={index}
              isOpen={openIndex === index}
              isLast={index === items.length - 1}
              onToggle={() => setOpenIndex((prev) => (prev === index ? null : index))}
            />
          ))}
        </dl>
      </div>
    </section>
  )
}
